import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  ArrowLeft,
  ShieldCheck,
  User as UserIcon,
  Building2,
  Home,
  MapPin,
  Phone,
  Mail,
  CalendarDays,
  IndianRupee,
  Layers,
  Hash,
  Briefcase,
  Info,
  Palette,
  Heart,
  ListChecks,
} from "lucide-react";
import { OutlineButton } from "../../components/Buttons";
import { toast } from "react-toastify";
import { getCustomerSales } from "../../services/sales";
import axiosInstance from "../../services/api/axios";
import BrowsePortfolios from "./BrowsePortfolios";
import ShortlistedPortfolios from "./ShortlistedPortfolios";
import Requests from "./Requests";

// Tabs are hash-driven so the URL reflects the active section.
// Hash ids are linked from RequestDetails.jsx (`#requests`) — do not rename.
const TAB_IDS = ["details", "browse-designs", "shortlisted-portfolios", "requests"];
const parseTabFromHash = (hash) => {
  if (!hash) return "details";
  const cleaned = hash.replace(/^#/, "").toLowerCase();
  return TAB_IDS.includes(cleaned) ? cleaned : "details";
};

const BuyerDetails = () => {
  const { buyerId } = useParams();
  const navigate = useNavigate();
  const navLocation = useLocation();

  const [activeTab, setActiveTab] = useState(() =>
    typeof window !== "undefined" ? parseTabFromHash(window.location.hash) : "details"
  );
  const [loading, setLoading] = useState(true);
  const [saleData, setSaleData] = useState(null);
  const [error, setError] = useState(null);

  // Extra counts used for the hero status pills (best-effort, errors are swallowed).
  const [shortlistCount, setShortlistCount] = useState(null);
  const [requestCount, setRequestCount] = useState(null);

  useEffect(() => {
    const fetchBuyerDetails = async () => {
      try {
        setLoading(true);
        setError(null);

        if (!buyerId) {
          throw new Error("Buyer ID not found");
        }

        // Fetch sale details using customerSalesId
        const salesResponse = await getCustomerSales({
          customerSaleId: parseInt(buyerId),
        });

        if (
          !salesResponse ||
          !Array.isArray(salesResponse) ||
          salesResponse.length === 0
        ) {
          throw new Error("Buyer data not found");
        }

        setSaleData(salesResponse[0]);
      } catch (err) {
        console.error("Error fetching buyer details:", err);
        setError(err.message || "Failed to fetch buyer details");
        toast.error(err.message || "Failed to fetch buyer details");
      } finally {
        setLoading(false);
      }
    };

    if (buyerId) {
      fetchBuyerDetails();
    }
  }, [buyerId]);

  // Keep activeTab in sync with the URL hash (back/forward, deep links).
  useEffect(() => {
    setActiveTab(parseTabFromHash(navLocation.hash));
  }, [navLocation.hash]);

  // Best-effort hero stat counts — fetched once we know which user+property to query.
  useEffect(() => {
    if (!saleData) return;
    const propertyId = saleData?.propertyData?.propertyId;
    const userId =
      saleData?.user?.userId || saleData?.user?.user_id || saleData?.user?.id;
    if (!propertyId || !userId) return;

    let cancelled = false;

    const fetchCounts = async () => {
      // Leads → request groups by designer
      try {
        const leadsResp = await axiosInstance.get("/interior-designers/leads", {
          params: { propertyId, userId, page: 1, page_size: 1000 },
        });
        const payload = leadsResp?.data ?? leadsResp;
        let leadsArray = [];
        if (Array.isArray(payload)) leadsArray = payload;
        else if (Array.isArray(payload?.items)) leadsArray = payload.items;
        else if (Array.isArray(payload?.data?.items)) leadsArray = payload.data.items;
        else if (Array.isArray(payload?.data)) leadsArray = payload.data;

        if (cancelled) return;
        const designerIds = new Set();
        leadsArray.forEach((l) => {
          const id = l?.interiorDesignerId || l?.interior_designer_id;
          if (id) designerIds.add(id);
        });
        setRequestCount(designerIds.size);
      } catch (err) {
        if (!cancelled) setRequestCount(null);
      }

      // Shortlist count = portfolio shortlists + featured-iteration shortlists
      // for this (buyer, property). The two BE endpoints (verified to exist):
      //   GET /interior-designers/portfolio-items/property-interactions
      //   GET /interior-designers/featured-iterations/my-interactions
      // Both accept userId + propertyId. We sum the lengths to mirror the
      // BE `has_shortlisted` filter which OR's the same two tables.
      const countItems = (resp) => {
        const payload = resp?.data ?? resp;
        if (Array.isArray(payload)) return payload.length;
        if (Array.isArray(payload?.items)) return payload.items.length;
        if (Array.isArray(payload?.data?.items)) return payload.data.items.length;
        if (Array.isArray(payload?.data)) return payload.data.length;
        return 0;
      };
      try {
        const [portfolioResp, featuredResp] = await Promise.all([
          axiosInstance
            .get("/interior-designers/portfolio-items/property-interactions", {
              params: { userId, propertyId },
            })
            .catch(() => null),
          axiosInstance
            .get("/interior-designers/featured-iterations/my-interactions", {
              params: { userId, propertyId },
            })
            .catch(() => null),
        ]);
        if (!cancelled) {
          const total = countItems(portfolioResp) + countItems(featuredResp);
          setShortlistCount(total);
        }
      } catch (err) {
        if (!cancelled) setShortlistCount(null);
      }
    };

    fetchCounts();
    return () => {
      cancelled = true;
    };
  }, [saleData]);

  const selectTab = (tabId) => {
    setActiveTab(tabId);
    if (typeof window !== "undefined") {
      navigate(`${navLocation.pathname}#${tabId}`, { replace: true });
    }
  };

  const handleBack = () => {
    navigate("/bv-user/buyers");
  };

  // Slate skeleton matching the rest of the visual language.
  if (loading) {
    return (
      <div className="px-4 sm:px-6 py-6 space-y-6">
        <div className="h-4 w-32 animate-pulse rounded bg-slate-100" />
        <div className="h-44 animate-pulse rounded-2xl bg-slate-100" />
        <div className="h-12 w-80 animate-pulse rounded-2xl bg-slate-100" />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="h-64 animate-pulse rounded-2xl bg-slate-100" />
          <div className="h-64 animate-pulse rounded-2xl bg-slate-100" />
        </div>
      </div>
    );
  }

  if (error || !saleData) {
    return (
      <div className="px-4 sm:px-6 py-6 space-y-6">
        <button
          type="button"
          onClick={handleBack}
          className="inline-flex items-center gap-1 text-sm text-slate-500 transition-colors hover:text-flamingo-600"
        >
          <ArrowLeft size={14} />
          Back to buyers
        </button>
        <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center shadow-sm">
          <div className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
            <UserIcon className="text-slate-400" size={22} />
          </div>
          <p className="mt-4 text-base font-medium text-slate-800">
            {error || "Buyer data not found"}
          </p>
          <p className="mt-1 text-sm text-slate-500">
            We couldn't load this buyer. Try going back and re-opening.
          </p>
          <OutlineButton onClick={handleBack} className="mt-4">
            Back to buyers
          </OutlineButton>
        </div>
      </div>
    );
  }

  const customer = saleData.user || {};
  const property = saleData.propertyData || {};
  const project = property?.projectPhase?.project || {};
  const config = property?.propertyConfig || {};
  const address = project?.address || {};

  const customerName =
    `${customer.firstName || ""} ${customer.lastName || ""}`.trim() || "Buyer";
  const unitNo = property.propertyNo || "—";
  const projectName = project.projectName || "—";
  const phoneNumber = customer.mobileNoCalling || customer.mobileNoWa || null;

  // Compose subline / address strings for the hero.
  const subline = [
    phoneNumber ? `${phoneNumber}` : null,
    `Unit ${unitNo}`,
    projectName !== "—" ? projectName : null,
  ]
    .filter(Boolean)
    .join(" · ");

  const fullAddress = (() => {
    const a = address || {};
    const parts = [
      [a.buildingNo, a.streetName1].filter(Boolean).join(" "),
      a.streetName2,
      a.district,
      a.city,
      a.state,
      a.pinCode,
    ].filter((s) => s && String(s).trim() !== "");
    return parts.length ? parts.join(", ") : null;
  })();

  // The sale has no flat "status" field; the meaningful status signals on the
  // payload are `cancellationStatus` (string enum) and `idStatus` (JSON map).
  // We show Cancelled in rose when cancelled, otherwise Active in emerald.
  const saleStatus = (() => {
    const cancellation =
      saleData?.cancellationStatus ||
      saleData?.cancellation_status ||
      null;
    if (cancellation && String(cancellation).toUpperCase() !== "ACTIVE") {
      return { label: "Cancelled", tone: "rose" };
    }
    // We only flag "Active" when we know there is a sale (any sale data).
    // No emerald pill on a missing sale to avoid lying about the state.
    if (saleData) return { label: "Active", tone: "emerald" };
    return null;
  })();

  // Tab metadata — drives the pill nav. Counts populate from background fetches.
  const TABS = [
    { id: "details", label: "Overview", icon: Info },
    { id: "browse-designs", label: "Browse Designs", icon: Palette },
    {
      id: "shortlisted-portfolios",
      label: "Shortlisted",
      icon: Heart,
      count: shortlistCount ?? undefined,
    },
    {
      id: "requests",
      label: "Requests",
      icon: ListChecks,
      count: requestCount ?? undefined,
    },
  ];

  // Stat tiles for the Overview tab.
  const statTiles = [
    {
      label: "Unit",
      value: unitNo,
      icon: Hash,
    },
    {
      label: "Configuration",
      value:
        config?.type || config?.configName || config?.propertyConfigName || "—",
      icon: Layers,
    },
    {
      label: "Carpet area",
      value: property.carpetArea ? `${property.carpetArea} sq.ft` : "—",
      icon: Home,
    },
    {
      label: "Base price",
      value: saleData.basePrice
        ? `₹${parseFloat(saleData.basePrice).toLocaleString("en-IN")}`
        : "—",
      icon: IndianRupee,
    },
  ];

  return (
    <div className="px-4 sm:px-6 py-6 space-y-6">
      {/* Back link above the hero */}
      <button
        type="button"
        onClick={handleBack}
        className="inline-flex items-center gap-1 text-sm text-slate-500 transition-colors hover:text-flamingo-600"
      >
        <ArrowLeft size={14} />
        Back to buyers
      </button>

      {/* Slate hero band — gradient with radial overlay, status row */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-800 to-slate-600 shadow-sm">
        <div
          className="absolute inset-0 opacity-[0.08]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 0%, rgba(255,255,255,0.6) 0, transparent 50%), radial-gradient(circle at 80% 100%, rgba(255,255,255,0.4) 0, transparent 50%)",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/40 via-slate-800/30 to-transparent" />

        <div className="relative px-6 py-7 sm:px-8 sm:py-8">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-[11px] font-medium uppercase tracking-wider text-white/80 backdrop-blur-sm">
            <ShieldCheck size={12} />
            Buildvizer User · Buyer
          </div>

          <h1 className="mt-3 text-2xl sm:text-3xl font-semibold text-white">
            {customerName}
          </h1>

          {subline && (
            <p className="mt-1 text-sm text-white/80">{subline}</p>
          )}

          {fullAddress && (
            <div className="mt-2 flex items-start gap-1.5 text-xs text-white/70">
              <MapPin size={13} className="mt-0.5 shrink-0" />
              <span className="line-clamp-2">{fullAddress}</span>
            </div>
          )}

          {/* Status row: sale status + counts */}
          <div className="mt-4 flex flex-wrap items-center gap-2">
            {saleStatus && (
              <span
                className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold text-white shadow-sm backdrop-blur-sm ${
                  saleStatus.tone === "rose"
                    ? "bg-rose-500/95"
                    : "bg-emerald-500/95"
                }`}
              >
                <ShieldCheck size={12} />
                {saleStatus.label}
              </span>
            )}
            <span className="inline-flex items-center gap-1 rounded-full bg-white/10 px-2.5 py-1 text-[11px] font-medium text-white/85 backdrop-blur-sm ring-1 ring-white/10">
              <ListChecks size={12} />
              {requestCount === null
                ? "Requests"
                : `${requestCount} ${requestCount === 1 ? "request" : "requests"}`}
            </span>
            <span className="inline-flex items-center gap-1 rounded-full bg-white/10 px-2.5 py-1 text-[11px] font-medium text-white/85 backdrop-blur-sm ring-1 ring-white/10">
              <Heart size={12} />
              {shortlistCount === null
                ? "Shortlist"
                : `${shortlistCount} shortlisted`}
            </span>
          </div>
        </div>
      </div>

      {/* Pill tab nav */}
      <div className="inline-flex flex-wrap gap-1 rounded-2xl border border-slate-200 bg-white p-1.5 shadow-sm">
        {TABS.map(({ id, label, icon: Icon, count }) => {
          const active = activeTab === id;
          return (
            <button
              key={id}
              type="button"
              onClick={() => selectTab(id)}
              className={`inline-flex items-center gap-1.5 rounded-xl px-4 py-2 text-sm font-medium transition-colors ${
                active
                  ? "bg-gradient-to-r from-flamingo-500 to-flamingo-600 text-white shadow-sm"
                  : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
              }`}
            >
              <Icon size={14} />
              {label}
              {typeof count === "number" && count > 0 && (
                <span
                  className={`tabular-nums rounded-md px-1.5 py-0.5 text-[10px] font-semibold ${
                    active ? "bg-white/20 text-white" : "bg-slate-100 text-slate-600"
                  }`}
                >
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Tab panels */}
      {activeTab === "details" && (
        <div className="space-y-6">
          {/* At-a-glance stat tiles */}
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
            <div className="mb-5">
              <h2 className="text-lg font-semibold text-slate-900">At a glance</h2>
              <p className="mt-0.5 text-sm text-slate-500">
                Quick snapshot of this buyer's unit and sale.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {statTiles.map(({ label, value, icon: Icon }) => (
                <div
                  key={label}
                  className="rounded-xl border border-slate-100 bg-slate-50/40 p-4"
                >
                  <div className="flex items-center gap-2 text-xs font-medium text-slate-500">
                    <Icon size={13} className="text-slate-400" />
                    {label}
                  </div>
                  <div className="mt-1.5 truncate text-xl font-semibold text-slate-900 tabular-nums">
                    {value}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Customer + Unit detail cards */}
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {/* Customer & project card */}
            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
              <div className="mb-5 flex items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-flamingo-50 text-flamingo-600">
                  <UserIcon size={18} />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">
                    Customer &amp; project
                  </h2>
                  <p className="mt-0.5 text-sm text-slate-500">
                    Contact and project basics.
                  </p>
                </div>
              </div>

              <dl className="space-y-3 text-sm">
                <div className="flex items-start justify-between gap-3">
                  <dt className="inline-flex items-center gap-2 text-slate-500">
                    <UserIcon size={13} className="text-slate-400" />
                    Customer
                  </dt>
                  <dd className="text-right font-medium text-slate-900">
                    {customerName}
                  </dd>
                </div>
                <div className="flex items-start justify-between gap-3">
                  <dt className="inline-flex items-center gap-2 text-slate-500">
                    <Mail size={13} className="text-slate-400" />
                    Email
                  </dt>
                  <dd className="text-right font-medium text-slate-900 break-all">
                    {customer.emailId || "—"}
                  </dd>
                </div>
                <div className="flex items-start justify-between gap-3">
                  <dt className="inline-flex items-center gap-2 text-slate-500">
                    <Phone size={13} className="text-slate-400" />
                    Phone
                  </dt>
                  <dd className="text-right font-medium text-slate-900 tabular-nums">
                    {phoneNumber || "—"}
                  </dd>
                </div>
                <div className="flex items-start justify-between gap-3">
                  <dt className="inline-flex items-center gap-2 text-slate-500">
                    <Briefcase size={13} className="text-slate-400" />
                    Project
                  </dt>
                  <dd className="text-right font-medium text-slate-900">
                    {projectName}
                  </dd>
                </div>
                <div className="flex items-start justify-between gap-3">
                  <dt className="inline-flex items-center gap-2 text-slate-500">
                    <MapPin size={13} className="text-slate-400" />
                    Location
                  </dt>
                  <dd className="text-right font-medium text-slate-900">
                    {address.city && address.state
                      ? `${address.city}, ${address.state}`
                      : "—"}
                  </dd>
                </div>
                {saleData.saleDate && (
                  <div className="flex items-start justify-between gap-3">
                    <dt className="inline-flex items-center gap-2 text-slate-500">
                      <CalendarDays size={13} className="text-slate-400" />
                      Sale date
                    </dt>
                    <dd className="text-right font-medium text-slate-900 tabular-nums">
                      {new Date(saleData.saleDate).toLocaleDateString("en-IN", {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                      })}
                    </dd>
                  </div>
                )}
                {saleData.basePrice && (
                  <div className="flex items-start justify-between gap-3">
                    <dt className="inline-flex items-center gap-2 text-slate-500">
                      <IndianRupee size={13} className="text-slate-400" />
                      Base price
                    </dt>
                    <dd className="text-right font-medium text-slate-900 tabular-nums">
                      ₹{parseFloat(saleData.basePrice).toLocaleString("en-IN")}
                    </dd>
                  </div>
                )}
              </dl>
            </div>

            {/* Unit card */}
            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
              <div className="mb-5 flex items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-600">
                  <Building2 size={18} />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Unit</h2>
                  <p className="mt-0.5 text-sm text-slate-500">
                    {config.description?.trim() || "Property unit details."}
                  </p>
                </div>
              </div>

              <dl className="space-y-3 text-sm">
                <div className="flex items-start justify-between gap-3">
                  <dt className="inline-flex items-center gap-2 text-slate-500">
                    <Hash size={13} className="text-slate-400" />
                    Unit no.
                  </dt>
                  <dd className="text-right font-medium text-slate-900 tabular-nums">
                    {unitNo}
                  </dd>
                </div>
                <div className="flex items-start justify-between gap-3">
                  <dt className="inline-flex items-center gap-2 text-slate-500">
                    <Building2 size={13} className="text-slate-400" />
                    Building
                  </dt>
                  <dd className="text-right font-medium text-slate-900">
                    {property.buildingNo || "—"}
                  </dd>
                </div>
                <div className="flex items-start justify-between gap-3">
                  <dt className="inline-flex items-center gap-2 text-slate-500">
                    <Home size={13} className="text-slate-400" />
                    Property type
                  </dt>
                  <dd className="text-right font-medium text-slate-900">
                    {property.propertyType || "—"}
                  </dd>
                </div>
                <div className="flex items-start justify-between gap-3">
                  <dt className="inline-flex items-center gap-2 text-slate-500">
                    <Layers size={13} className="text-slate-400" />
                    Project phase
                  </dt>
                  <dd className="text-right font-medium text-slate-900">
                    {property?.projectPhase?.name || "—"}
                  </dd>
                </div>
                {property.carpetArea && (
                  <div className="flex items-start justify-between gap-3">
                    <dt className="inline-flex items-center gap-2 text-slate-500">
                      <Home size={13} className="text-slate-400" />
                      Carpet area
                    </dt>
                    <dd className="text-right font-medium text-slate-900 tabular-nums">
                      {property.carpetArea} sq.ft
                    </dd>
                  </div>
                )}
                {config.type && (
                  <div className="flex items-start justify-between gap-3">
                    <dt className="inline-flex items-center gap-2 text-slate-500">
                      <Layers size={13} className="text-slate-400" />
                      Configuration type
                    </dt>
                    <dd className="text-right font-medium text-slate-900">
                      {config.type}
                    </dd>
                  </div>
                )}
                {property.floorNo && (
                  <div className="flex items-start justify-between gap-3">
                    <dt className="inline-flex items-center gap-2 text-slate-500">
                      <Layers size={13} className="text-slate-400" />
                      Floor
                    </dt>
                    <dd className="text-right font-medium text-slate-900 tabular-nums">
                      {property.floorNo}
                    </dd>
                  </div>
                )}
              </dl>
            </div>
          </div>
        </div>
      )}

      {activeTab === "browse-designs" && (
        <BrowsePortfolios
          buyerId={customer?.userId ?? customer?.user_id}
          saleData={saleData}
        />
      )}

      {activeTab === "shortlisted-portfolios" && (
        <ShortlistedPortfolios
          buyerId={customer?.userId ?? customer?.user_id}
          saleData={saleData}
        />
      )}

      {activeTab === "requests" && (
        <Requests buyerId={buyerId} saleData={saleData} />
      )}
    </div>
  );
};

export default BuyerDetails;
